import type { IPairing } from "@vmosolutions/oc-interfaces";
import { Grid } from "@mui/material";
import { convertMilli2DaysHrsMin } from "src/utils/index";
import type { IPairingEditorEventGroup } from "../PairingEventTable";
import { DateDisplay } from "src/components/DateDisplay";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { mergeDraft } from "../PairingEditorWindow";
import _ from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { updatePairingEditor } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import type { IRootState } from "src/redux/reducers/CombinedReducers";

type IDutyPeriodSummary = {
  id: string;
  eventGroupIndex: number;
  eventGroup: IPairingEditorEventGroup;
  displayAsUTC: boolean;
};

// maxTime allowed for checking before or after after (used for first and last eventgroups);
// Set to 8 hours in milliseconds.
const maxTime = 28800000;

export const DutyPeriodCheckin = (props: IDutyPeriodSummary): JSX.Element => {
  const { id, eventGroupIndex, eventGroup, displayAsUTC } = props;
  const dispatch = useDispatch();
  const storedPairing = useSelector((state: IRootState): IPairing | undefined => state.crewSchedule.pairingEditors[id]);

  if ((eventGroup.sortedEvents?.length ?? 0) > 0 && storedPairing) {
    const { eventGroups, checkInOutOverrides } = storedPairing;

    // It is possible for both of these to be true (i.e one event group);
    const isFirst = eventGroupIndex === 0;

    // CHECKING TIME to display as well as allowable minMax values for possible override.
    const firstEvent = eventGroup.sortedEvents?.[0];
    const firstEventStartMs = firstEvent?.start?.valueOf();
    const startICAO = firstEvent?.startICAO;
    const tzICAO = !displayAsUTC ? startICAO : undefined;

    const checkInOverride = checkInOutOverrides?.find((o) => o.eventGroup === eventGroupIndex && o.inout);

    const checkInTime = (checkInOverride?.time ?? eventGroup.start).valueOf();
    // Get prev eventgroup that contains flights or non flying assignments. (skip rests)
    //! Should be safe to jump back 2 eventgroups if the one before contains a rest.
    const prevEventIndex =
      eventGroups[eventGroupIndex - 1]?.restPeriods.length === 0 ? eventGroupIndex - 1 : eventGroupIndex - 2;
    const prevEventWithFlights = eventGroups[prevEventIndex];
    //! `eventGroupIndex` is the index into the rendered display array, which can sit one render ahead of the
    //! pairing in the store (a save swaps `eventGroups` out before the display array re-syncs), so the
    //! neighbouring group may not be there. Fall back to the maxTime bound rather than dereferencing undefined.
    const minCheckIn =
      isFirst || prevEventIndex < 0 || prevEventWithFlights == null
        ? (firstEventStartMs ?? 0) - maxTime
        : prevEventWithFlights.end.valueOf();
    const maxCheckIn = firstEventStartMs;
    const checkInWarning =
      maxCheckIn != null && checkInTime <= maxCheckIn && checkInTime >= minCheckIn
        ? null
        : "Check-In out of acceptable bounds or colliding with other events in the pairing.";

    const handleCheckOverride = async (newState: boolean, inout: boolean): Promise<void> => {
      const updatedCheckInOutOverrides = [...(checkInOutOverrides ?? [])];
      if (newState) {
        // ADD
        updatedCheckInOutOverrides.push({
          inout: inout,
          time: inout ? eventGroup.start.valueOf() : eventGroup.end.valueOf(), // store current eventgroup checkin as starting time.
          eventGroup: eventGroupIndex,
        });
      } else {
        // REMOVE
        const overrideIndex = updatedCheckInOutOverrides.findIndex(
          (o) => o.eventGroup === eventGroupIndex && o.inout === inout
        );
        updatedCheckInOutOverrides.splice(overrideIndex, 1);
      }

      // If override is turned off, run draftPairing to re-apply default checkin time.
      if (!newState) {
        const draft = await mergeDraft({ ...storedPairing, checkInOutOverrides: updatedCheckInOutOverrides }, dispatch);
        // A failed draft keeps the override, as removing it alone would leave the overridden time in place.
        if (draft) {
          dispatch(updatePairingEditor(id, draft.pairing));
        }
      } else {
        dispatch(
          updatePairingEditor(id, {
            checkInOutOverrides: updatedCheckInOutOverrides,
          })
        );
      }
    };

    // Change value of assigned override.
    const handleChangeCheckTime = (value: number, inout: boolean): void => {
      const updatedCheckInOutOverrides = _.cloneDeep(checkInOutOverrides); // use deep clone as to not mutate.
      const overrideIndex = updatedCheckInOutOverrides?.findIndex(
        (o) => o.eventGroup === eventGroupIndex && o.inout === inout
      );
      if (overrideIndex != null && overrideIndex > -1 && updatedCheckInOutOverrides != null) {
        updatedCheckInOutOverrides[overrideIndex].time = value;
        dispatch(
          updatePairingEditor(id, {
            checkInOutOverrides: updatedCheckInOutOverrides,
          })
        );
      }
    };

    const checkinDuration =
      firstEventStartMs != null && firstEventStartMs - checkInTime > 0
        ? ` (${convertMilli2DaysHrsMin(firstEventStartMs - checkInTime)})`
        : " (No Check-In)";

    return (
      <Grid container className="duty_period_summary">
        <Grid container className="duty_row checkin" alignItems="center">
          <span style={{ fontWeight: 500 }}>Check-In: </span>
          <CheckboxField
            className="field checkboxfield"
            label="Override"
            name={`eventgroup_${eventGroupIndex}_checkin_override`}
            checked={!!checkInOverride}
            onChange={_.debounce(() => {
              void handleCheckOverride(!checkInOverride, true);
            }, 500)}
          />
          {checkInOverride ? (
            <DateTimeField
              label=""
              className="field datetimefield"
              name={`eventgroup_${eventGroupIndex}_checkin`}
              value={checkInTime}
              onChange={(value: string | number) => handleChangeCheckTime(Number(value), true)}
              isEpoch={true}
              showAirportTime={startICAO}
              localizeDisplay={!displayAsUTC}
              hideLocalizeBtn={true}
            />
          ) : (
            <DateDisplay date={checkInTime} tzICAO={tzICAO} />
          )}
          {checkinDuration}
        </Grid>

        {checkInOverride && checkInWarning && (
          <Grid item>
            <span className="override_warning">{checkInWarning}</span>
          </Grid>
        )}
      </Grid>
    );
  }
  return <></>;
};
